import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus, Mail, Lock, User } from "lucide-react";

const LOGO_URL = "https://cdn.gamma.app/40wtxlv020nnuu2/0bd85e15bbd243ca96d4ed4e00a0f4fa/original/HumaDash-Logo-3.4.25.png";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "", password: "", confirmPassword: "", role: "donor" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    setError("");
    try {
      await register({ name: formData.name, email: formData.email, password: formData.password, role: formData.role });
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center bg-[#F4F5F7]">
      <div className="absolute inset-0 hero-glow opacity-20 pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="HumaDash" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl text-slate-800">Create Your Account</h1>
          <p className="text-slate-500 mt-1">Join HumaDash and make a difference</p>
        </div>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600" data-testid="register-error">{error}</div>}
              <div className="space-y-2">
                <Label className="text-slate-700">Full Name</Label>
                <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="John Doe" className="pl-10 border-slate-200" required data-testid="register-name" /></div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Email</Label>
                <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="you@example.com" className="pl-10 border-slate-200" required data-testid="register-email" /></div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Role</Label>
                <Select value={formData.role} onValueChange={(val) => handleChange("role", val)}>
                  <SelectTrigger className="border-slate-200" data-testid="register-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="donor">Donor</SelectItem>
                    <SelectItem value="ngo">NGO / Charity</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Password</Label>
                <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="password" value={formData.password} onChange={(e) => handleChange("password", e.target.value)} placeholder="Create password" className="pl-10 border-slate-200" required data-testid="register-password" /></div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Confirm Password</Label>
                <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="password" value={formData.confirmPassword} onChange={(e) => handleChange("confirmPassword", e.target.value)} placeholder="Confirm password" className="pl-10 border-slate-200" required data-testid="register-confirm-password" /></div>
              </div>
              <Button type="submit" className="w-full bg-[#6B21A8] hover:bg-[#581C87] text-white" disabled={loading} data-testid="register-submit">
                {loading ? "Creating account..." : <><UserPlus className="h-4 w-4 mr-2" />Create Account</>}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-500">
              Already have an account?{" "}<Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
